import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { PeriodSelect } from "@/components/period-select";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { Period } from "@/lib/period";

interface PeriodBarProps {
  days: Period;
  onChange: (days: Period) => void;
}

/**
 * The filter bar a part of Statistieken opens with, in the tinted box: its
 * period (Afgelopen week, maand, kwartaal), which every figure under it
 * follows. Keep the period in the URL with `usePeriod`.
 */
export function PeriodBar({ days, onChange }: PeriodBarProps) {
  return (
    // A period is a view, not a filter: no reset, and the whole row on a phone.
    <div className="bg-accent-light rounded-lg p-3">
      <PeriodSelect value={days} onChange={onChange} className="sm:w-56" />
    </div>
  );
}

/** A part's heading over a chart or a table. */
export function SectionHeading({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <h2 id={id} className="font-heading text-primary mb-3 text-sm font-bold">
      {children}
    </h2>
  );
}

/** A chart in a card, under its heading. */
export function ChartSection({
  title,
  aside,
  children,
}: {
  title: string;
  /** Right of the heading, such as "Aantal | Percentage". */
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <SectionHeading>{title}</SectionHeading>
        {aside && <div className="mb-3">{aside}</div>}
      </div>
      <Card>
        <CardContent className="py-6 max-sm:px-4">{children}</CardContent>
      </Card>
    </section>
  );
}

/** A chart's place when the period has nothing to show. */
export function EmptyChart({ children }: { children: ReactNode }) {
  return <p className="text-muted py-8 text-center text-sm">{children}</p>;
}

/** Under the filter bar when a refresh failed and the figures on screen are the last ones loaded. */
export function StaleNote({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <p role="status" className="text-muted text-sm">
      {t("analytics.stale")}{" "}
      <button type="button" onClick={onRetry} className="underline">
        {t("common.tryAgain")}
      </button>
    </p>
  );
}

interface RowLinkProps {
  to: string;
  /** Where it goes, for whom: its name and its tooltip ("Oproepen van Gerd bekijken"). */
  label: string;
}

/**
 * A row's way to another page, at the row's end: a small ghost chevron that
 * says where it goes in a themed tooltip and to screen readers.
 */
export function RowLink({ to, label }: RowLinkProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button asChild variant="ghost" size="icon" className="h-8 w-8 pointer-coarse:size-10">
          <Link to={to} aria-label={label}>
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
